/**
 * SPEC-20260507-2100: tela admin de logs de invocação IA.
 *
 * Tabela com filtros (provider, kind, status, actor, date range).
 * Click em row → drawer com payload JSON expanded.
 * RBAC: platform-master.
 */

import { useEffect, useState } from "react";
import { Loader2, X, ChevronLeft, ChevronRight, Filter, Activity } from "lucide-react";
import { toast } from "sonner";
import {
  listAIInvocations,
  getAIInvocation,
  type AIInvocationLogRow,
  type AIInvocationLogFilters,
  type AIInvocationLogListResponse,
} from "../services/ai-invocations";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RequireRole } from "../components/RequireRole";
import { cn } from "../utils/cn";

export default function AIInvocationsPage() {
  return (
    <RequireRole anyOf={["platform-master"]}>
      <Content />
    </RequireRole>
  );
}

function Content() {
  const [data, setData] = useState<AIInvocationLogListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filters, setFilters] = useState<AIInvocationLogFilters>({ limit: 50, offset: 0 });
  const [selected, setSelected] = useState<AIInvocationLogRow | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await listAIInvocations(filters);
      setData(r);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao listar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.limit, filters.offset]);

  function applyFilters() {
    setFilters((f) => ({ ...f, offset: 0 }));
    void refresh();
  }

  function setFilter<K extends keyof AIInvocationLogFilters>(
    key: K,
    value: AIInvocationLogFilters[K],
  ) {
    setFilters((f) => ({ ...f, [key]: value }));
  }

  async function openDetail(row: AIInvocationLogRow) {
    setSelected(row); // mostra dados que já temos da lista
    setSelectedLoading(true);
    try {
      const full = await getAIInvocation(row.id);
      setSelected(full);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao buscar detalhe");
    } finally {
      setSelectedLoading(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Invocações IA"
        subtitle="Log completo de todas as chamadas a providers IA. Filtre por provider, kind, status, actor."
      />

      <div className="px-6 py-4 space-y-3">
        {err && (
          <div className="rounded border border-danger/30 bg-danger-soft/40 p-3 text-sm text-danger-soft-fg">
            {err}
          </div>
        )}

        <FiltersBar filters={filters} setFilter={setFilter} onApply={applyFilters} />

        {loading && !data && (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> carregando…
          </div>
        )}

        {data && data.data.length === 0 && (
          <div className="text-sm text-muted-foreground italic text-center py-8 flex flex-col items-center gap-2">
            <Activity size={24} className="text-muted-foreground/50" />
            Nenhuma invocação registrada com estes filtros.
          </div>
        )}

        {data && data.data.length > 0 && (
          <>
            <div className="rounded border border-border bg-surface-1 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 w-32">Quando</th>
                    <th className="text-left px-3 py-2">Kind</th>
                    <th className="text-left px-3 py-2">Provider / Model</th>
                    <th className="text-left px-3 py-2 w-24">Status</th>
                    <th className="text-right px-3 py-2 w-20">Tokens</th>
                    <th className="text-right px-3 py-2 w-20">Latency</th>
                    <th className="text-left px-3 py-2 w-32">Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => void openDetail(row)}
                      className="border-t border-border-subtle cursor-pointer hover:bg-surface-2 transition-colors"
                    >
                      <td className="px-3 py-2 font-mono-tight text-xs text-muted-foreground">
                        {new Date(row.startedAt).toLocaleString("pt-BR", {
                          dateStyle: "short",
                          timeStyle: "medium",
                        })}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <code className="font-mono-tight">{row.integrationKind}</code>
                        {row.iteration > 0 && (
                          <span className="ml-1 text-[10px] text-muted-foreground">
                            #{row.iteration}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{row.provider}</div>
                        <div className="text-[10px] text-muted-foreground font-mono-tight">
                          {row.model}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tight text-xs text-muted-foreground">
                        {row.inputTokens !== null && row.outputTokens !== null
                          ? `${row.inputTokens}+${row.outputTokens}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tight text-xs text-muted-foreground">
                        {row.durationMs !== null ? `${row.durationMs}ms` : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {row.actorUsername ?? row.actorSub ?? <span className="italic text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              total={data.pagination.total}
              limit={data.pagination.limit}
              offset={data.pagination.offset}
              onOffset={(off) => setFilter("offset", off)}
            />
          </>
        )}
      </div>

      {selected && (
        <DetailDrawer
          row={selected}
          loading={selectedLoading}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium",
        status === "success" && "bg-success-soft text-success-soft-fg",
        status === "failed" && "bg-danger-soft text-danger-soft-fg",
        status === "pending" && "bg-warning-soft text-warning-soft-fg",
      )}
    >
      {status}
    </span>
  );
}

interface FiltersBarProps {
  filters: AIInvocationLogFilters;
  setFilter<K extends keyof AIInvocationLogFilters>(key: K, value: AIInvocationLogFilters[K]): void;
  onApply(): void;
}

function FiltersBar({ filters, setFilter, onApply }: FiltersBarProps) {
  return (
    <div className="rounded border border-border bg-surface-1 p-3">
      <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-muted-foreground">
        <Filter size={12} /> Filtros
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <div>
          <Label htmlFor="f-provider" className="text-[10px] uppercase">Provider</Label>
          <select
            id="f-provider"
            value={filters.provider ?? ""}
            onChange={(e) => setFilter("provider", e.target.value || undefined)}
            className="w-full px-2 py-1 text-xs border border-border rounded bg-bg"
          >
            <option value="">todos</option>
            <option value="anthropic">anthropic</option>
            <option value="openai">openai</option>
            <option value="gemini">gemini</option>
            <option value="bedrock">bedrock</option>
            <option value="codex">codex</option>
            <option value="claude-code">claude-code</option>
          </select>
        </div>
        <div>
          <Label htmlFor="f-kind" className="text-[10px] uppercase">Kind</Label>
          <select
            id="f-kind"
            value={filters.integrationKind ?? ""}
            onChange={(e) => setFilter("integrationKind", e.target.value || undefined)}
            className="w-full px-2 py-1 text-xs border border-border rounded bg-bg"
          >
            <option value="">todos</option>
            <option value="chat-stateless">chat-stateless</option>
            <option value="chat-session">chat-session</option>
            <option value="generate-script">generate-script</option>
            <option value="validate-script">validate-script</option>
            <option value="generate-script-interactive">generate-script-interactive</option>
            <option value="credential-test">credential-test</option>
            <option value="tool-loop-iteration">tool-loop-iteration</option>
            <option value="saga-variable-autofill">saga-variable-autofill</option>
            <option value="other">other</option>
          </select>
        </div>
        <div>
          <Label htmlFor="f-status" className="text-[10px] uppercase">Status</Label>
          <select
            id="f-status"
            value={filters.status ?? ""}
            onChange={(e) => setFilter("status", (e.target.value || undefined) as never)}
            className="w-full px-2 py-1 text-xs border border-border rounded bg-bg"
          >
            <option value="">todos</option>
            <option value="success">success</option>
            <option value="failed">failed</option>
            <option value="pending">pending</option>
          </select>
        </div>
        <div>
          <Label htmlFor="f-actor" className="text-[10px] uppercase">Actor sub</Label>
          <Input
            id="f-actor"
            value={filters.actorSub ?? ""}
            onChange={(e) => setFilter("actorSub", e.target.value || undefined)}
            placeholder="sub..."
            className="text-xs"
          />
        </div>
        <div>
          <Label htmlFor="f-from" className="text-[10px] uppercase">De</Label>
          <Input
            id="f-from"
            type="datetime-local"
            value={filters.dateFrom ?? ""}
            onChange={(e) => setFilter("dateFrom", e.target.value || undefined)}
            className="text-xs"
          />
        </div>
        <div>
          <Label htmlFor="f-to" className="text-[10px] uppercase">Até</Label>
          <Input
            id="f-to"
            type="datetime-local"
            value={filters.dateTo ?? ""}
            onChange={(e) => setFilter("dateTo", e.target.value || undefined)}
            className="text-xs"
          />
        </div>
      </div>
      <div className="flex justify-end mt-2">
        <Button size="sm" onClick={onApply}>
          Aplicar
        </Button>
      </div>
    </div>
  );
}

interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onOffset(off: number): void;
}

function Pagination({ total, limit, offset, onOffset }: PaginationProps) {
  const page = Math.floor(offset / limit) + 1;
  const lastPage = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span>
        {total} resultado{total === 1 ? "" : "s"} · página {page}/{lastPage}
      </span>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="outline"
          disabled={offset === 0}
          onClick={() => onOffset(Math.max(0, offset - limit))}
        >
          <ChevronLeft size={12} /> Anterior
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={offset + limit >= total}
          onClick={() => onOffset(offset + limit)}
        >
          Próxima <ChevronRight size={12} />
        </Button>
      </div>
    </div>
  );
}

interface DetailDrawerProps {
  row: AIInvocationLogRow;
  loading: boolean;
  onClose(): void;
}

function DetailDrawer({ row, loading, onClose }: DetailDrawerProps) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-end z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-1 w-full max-w-3xl h-full overflow-y-auto border-l border-border shadow-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-medium text-base">
              <code className="font-mono-tight text-sm">{row.integrationKind}</code>
              {row.iteration > 0 && (
                <span className="ml-1.5 text-xs text-muted-foreground">iteration #{row.iteration}</span>
              )}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {row.provider} / <code className="font-mono-tight">{row.model}</code> ·{" "}
              <StatusBadge status={row.status} /> ·{" "}
              {new Date(row.startedAt).toLocaleString("pt-BR")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 size={12} className="animate-spin" /> carregando detalhes…
          </div>
        )}

        <div className="space-y-3">
          <Field label="ID" value={<code className="font-mono-tight text-xs">{row.id}</code>} />
          {row.parentInvocationId && (
            <Field label="Parent invocation" value={<code className="font-mono-tight text-xs">{row.parentInvocationId}</code>} />
          )}
          {row.sessionId && (
            <Field label="Session" value={<code className="font-mono-tight text-xs">{row.sessionId}</code>} />
          )}
          {row.actorUsername || row.actorSub ? (
            <Field
              label="Actor"
              value={
                <span>
                  {row.actorUsername ?? "—"}{" "}
                  {row.actorSub && <span className="text-muted-foreground">({row.actorSub})</span>}
                </span>
              }
            />
          ) : null}
          <Field
            label="Duration"
            value={row.durationMs !== null ? `${row.durationMs} ms` : "—"}
          />
          <Field
            label="Tokens"
            value={
              row.inputTokens !== null && row.outputTokens !== null
                ? `${row.inputTokens} in + ${row.outputTokens} out${row.thinkingTokens ? ` + ${row.thinkingTokens} thinking` : ""}`
                : "—"
            }
          />
          {row.errorMessage && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Error ({row.errorClass ?? "Unknown"})
              </Label>
              <pre className="mt-1 p-2 rounded bg-danger-soft/30 border border-danger/30 text-xs text-danger-soft-fg whitespace-pre-wrap">
                {row.errorMessage}
              </pre>
            </div>
          )}
          {row.systemPrompt && (
            <CollapsibleText label="System Prompt" text={row.systemPrompt} />
          )}
          {row.userPrompt && <CollapsibleText label="User Prompt" text={row.userPrompt} />}
          {row.responseText && <CollapsibleText label="Response Text" text={row.responseText} />}
          {row.thinkingContent && (
            <CollapsibleText label="Thinking" text={row.thinkingContent} />
          )}
          {row.toolsAvailable !== null && row.toolsAvailable !== undefined && (
            <CollapsibleJson label="Tools Available" value={row.toolsAvailable} />
          )}
          {row.toolsRequested !== null && row.toolsRequested !== undefined && (
            <CollapsibleJson label="Tools Requested" value={row.toolsRequested} />
          )}
          {row.toolResults !== null && row.toolResults !== undefined && (
            <CollapsibleJson label="Tool Results" value={row.toolResults} />
          )}
          {row.requestPayload !== null && row.requestPayload !== undefined && (
            <CollapsibleJson label="Request Payload" value={row.requestPayload} />
          )}
          {row.responsePayload !== null && row.responsePayload !== undefined && (
            <CollapsibleJson label="Response Payload" value={row.responsePayload} />
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 text-xs">
      <span className="text-muted-foreground uppercase tracking-wider text-[10px] font-medium">
        {label}
      </span>
      <div>{value}</div>
    </div>
  );
}

function CollapsibleText({ label, text }: { label: string; text: string }) {
  return (
    <details className="rounded border border-border-subtle bg-bg p-2">
      <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label} ({text.length} chars)
      </summary>
      <pre className="mt-1.5 text-xs whitespace-pre-wrap font-mono-tight overflow-x-auto">
        {text}
      </pre>
    </details>
  );
}

function CollapsibleJson({ label, value }: { label: string; value: unknown }) {
  const json = JSON.stringify(value, null, 2);
  return (
    <details className="rounded border border-border-subtle bg-bg p-2">
      <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label} ({json.length} bytes)
      </summary>
      <pre className="mt-1.5 text-xs whitespace-pre-wrap font-mono-tight overflow-x-auto">
        {json}
      </pre>
    </details>
  );
}
